import { useState } from "react";
import { Link } from "react-router-dom";
import Header from "@/components/layout/Header";
import Footer from "@/components/layout/Footer";
import WhatsAppFloat from "@/components/ui/whatsapp-float";
import SEO from "@/components/SEO";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import Container from "@/components/ui/container";
import Breadcrumbs from "@/components/ui/breadcrumbs";
import FAQSection from "@/components/ui/faq-section";
import { AuroraHero } from "@/components/ui/aurora-hero";
import { 
  Users, 
  UserPlus, 
  Calendar, 
  UserX,
  FileText,
  Clock,
  CheckCircle,
  ArrowRight,
  Sparkles,
  TrendingUp,
  Shield,
  Zap,
  MonitorPlay,
  DollarSign,
  UserCheck,
  Briefcase,
  Heart,
  Award
} from "lucide-react";
import RelatedLinks from "@/components/ui/related-links";

const ServicesRH = () => {
  const [activeTab, setActiveTab] = useState("admissao");

  const services = [
    {
      id: "admissao",
      icon: UserPlus,
      title: "Admissão Digital",
      description: "Processo completo de admissão 100% digital com coleta eletrônica de documentos",
      color: "from-fs-cyan-500 to-fs-magenta-500",
      details: {
        overview: "Revolucione seu processo de admissão com nossa solução 100% digital. Elimine papelada, acelere o onboarding e proporcione uma experiência moderna para novos colaboradores desde o primeiro dia.",
        features: [
          "Onboarding digital completo",
          "Coleta eletrônica de documentos",
          "Assinatura digital de contratos",
          "Checklist automático de integração",
          "Portal do novo colaborador",
          "Integração automática com folha de pagamento"
        ],
        benefits: [
          "Redução de 90% no tempo de admissão",
          "Processo 100% paperless",
          "Experiência moderna para colaboradores",
          "Compliance garantido com CLT"
        ],
        processes: [
          "Coleta de documentos pessoais",
          "Exames médicos e ASO",
          "Contrato de trabalho digital",
          "Cadastro biométrico",
          "Criação de credenciais de acesso",
          "Treinamento inicial online"
        ]
      }
    },
    {
      id: "ferias",
      icon: Calendar,
      title: "Gestão de Férias",
      description: "Sistema completo para solicitação, aprovação e controle de férias e abonos",
      color: "from-fs-magenta-500 to-fs-purple-500",
      details: {
        overview: "Gerencie férias e abonos de forma eficiente com workflows automatizados, controle de saldos em tempo real e integração direta com a folha de pagamento, garantindo compliance trabalhista.",
        features: [
          "Solicitação online de férias",
          "Aprovação por workflow hierárquico",
          "Controle automático de saldos",
          "Programação antecipada de férias",
          "Gestão de abonos pecuniários",
          "Relatórios gerenciais completos"
        ],
        benefits: [
          "Redução de 85% no tempo de aprovação",
          "Controle total de saldos",
          "Programação eficiente de equipes",
          "Compliance com legislação trabalhista"
        ],
        processes: [
          "Solicitação pelo colaborador",
          "Aprovação do gestor imediato",
          "Validação do RH",
          "Agendamento automático",
          "Integração com folha",
          "Comunicação automática"
        ]
      }
    },
    {
      id: "desligamento",
      icon: UserX,
      title: "Processo de Desligamento",
      description: "Workflow completo para desligamentos com checklist digital e documentação automática",
      color: "from-fs-purple-500 to-fs-cyan-500",
      details: {
        overview: "Conduza desligamentos de forma profissional e eficiente com nosso sistema completo que garante todos os procedimentos legais, documentação adequada e transição suave de responsabilidades.",
        features: [
          "Checklist digital de desligamento",
          "Transferência de responsabilidades",
          "Coleta de equipamentos",
          "Cálculo automático de verbas rescisórias",
          "Geração de documentos obrigatórios",
          "Entrevista de desligamento digital"
        ],
        benefits: [
          "Processo 100% documentado",
          "Redução de passivos trabalhistas",
          "Transição organizada de atividades",
          "Compliance total com CLT"
        ],
        processes: [
          "Iniciação do processo",
          "Checklist de pendências",
          "Devolução de equipamentos",
          "Cálculos rescisórios",
          "Documentação final",
          "Entrevista de feedback"
        ]
      }
    },
    {
      id: "processos",
      icon: FileText,
      title: "Processos Humanos Ágeis",
      description: "Automação de todos os processos de RH com workflows inteligentes e aprovações",
      color: "from-fs-cyan-500 to-fs-purple-500",
      details: {
        overview: "Automatize todos os processos de Recursos Humanos com workflows inteligentes que reduzem tempo, eliminam retrabalho e garantem que nada seja esquecido, desde solicitações simples até processos complexos.",
        features: [
          "Workflows customizáveis para qualquer processo",
          "Aprovações hierárquicas automáticas",
          "Notificações e lembretes inteligentes",
          "Dashboard gerencial em tempo real",
          "Relatórios automatizados",
          "Integração com sistemas corporativos"
        ],
        benefits: [
          "Redução de 75% no tempo de processos",
          "Eliminação de retrabalho",
          "Visibilidade total dos processos",
          "Padronização de procedimentos"
        ],
        processes: [
          "Mudança de função/salário",
          "Horas extras e banco de horas",
          "Atestados e afastamentos",
          "Treinamentos e capacitações",
          "Avaliações de desempenho",
          "Benefícios e vales"
        ]
      }
    }
  ];

  const activeService = services.find(s => s.id === activeTab);

  const stats = [
    { icon: Users, number: "10K+", label: "Colaboradores Atendidos" },
    { icon: Clock, number: "90%", label: "Redução de Tempo" },
    { icon: Shield, number: "100%", label: "Compliance CLT" },
    { icon: TrendingUp, number: "85%", label: "Satisfação RH" }
  ];

  const benefits = [
    {
      icon: Zap,
      title: "Agilidade nos Processos",
      description: "Automação que reduz drasticamente o tempo de execução de processos de RH"
    },
    {
      icon: Shield,
      title: "Compliance Garantido",
      description: "Todos os processos seguem rigorosamente a legislação trabalhista brasileira"
    },
    {
      icon: UserCheck,
      title: "Experiência do Colaborador",
      description: "Interface moderna e intuitiva que melhora a experiência de todos os usuários"
    },
    {
      icon: MonitorPlay,
      title: "Visibilidade Total",
      description: "Dashboards e relatórios que oferecem visão completa dos processos de RH"
    }
  ];

  return (
    <div className="min-h-screen bg-background">
      <SEO 
        title="Recursos Humanos Digital com Fluig"
        description="Admissão digital, gestão de férias, desligamentos e processos de RH 100% automatizados. Redução de 90% no tempo, compliance CLT e integração com folha de pagamento."
        keywords={['recursos humanos digital', 'admissão digital fluig', 'gestão férias fluig', 'processos rh automatizados', 'rh paperless', 'onboarding digital', 'desligamento digital', 'integração folha pagamento']}
        type="website"
      />
      <Header />
      
      <main>
        {/* Hero Section */}
        <section id="hero">
          <AuroraHero>
            <div className="text-center max-w-4xl mx-auto">
              {/* Badge */}
              <div className="inline-flex items-center space-x-2 bg-fs-cyan-500/10 backdrop-blur-sm border border-fs-cyan-500/30 rounded-full px-6 py-3 mb-8">
                <Users className="w-5 h-5 text-fs-cyan-400" />
                <span className="text-foreground dark:text-white/90 font-medium">
                  Recursos Humanos
                </span>
              </div>

              {/* Title */}
              <h1 className="text-5xl md:text-6xl font-bold font-poppins text-foreground dark:text-white mb-6">
                Processos Humanos
                <span className="block fs-gradient-text mt-2">
                  Ágeis e Digitais
                </span>
              </h1>

              {/* Description */}
              <p className="text-xl text-muted-foreground dark:text-white/70 leading-relaxed mb-12 max-w-3xl mx-auto">
                Automatize completamente seus processos de RH com soluções digitais que
                reduzem tempo, eliminam papelada e melhoram a experiência dos colaboradores.
              </p>

              {/* Stats */}
              <div className="grid grid-cols-2 md:grid-cols-4 gap-6 mb-12">
                {stats.map((stat, index) => (
                  <div key={stat.label} className="text-center">
                    <div className="inline-flex items-center justify-center w-12 h-12 rounded-xl bg-gradient-to-br from-fs-cyan-500 to-fs-magenta-500 mb-3">
                      <stat.icon className="w-6 h-6 text-white" />
                    </div>
                    <div className="text-2xl md:text-3xl font-bold fs-gradient-text mb-1">
                      {stat.number}
                    </div>
                    <div className="text-sm text-muted-foreground dark:text-white/70">
                      {stat.label}
                    </div>
                  </div>
                ))}
              </div>

              {/* CTA */}
              <div className="flex flex-col sm:flex-row items-center justify-center space-y-4 sm:space-y-0 sm:space-x-6">
                <Button variant="fs-primary" size="lg" className="group">
                  Solicitar Demonstração
                  <ArrowRight className="w-5 h-5 ml-2 group-hover:translate-x-1 transition-transform" />
                </Button>
                <Button variant="fs-outline" size="lg">
                  Ver Processos Disponíveis
                </Button>
              </div>
            </div>
          </AuroraHero>
        </section>

        {/* Benefits Section */}
        <section className="py-20 bg-gradient-to-b from-fs-section to-fs-section/30">
          <Container>
            <div className="text-center mb-16">
              <h2 className="text-3xl md:text-4xl font-bold font-poppins text-foreground dark:text-white mb-4">
                Por que escolher nossos
                <span className="block fs-gradient-text mt-2">
                  Serviços de RH?
                </span>
              </h2>
              <p className="text-muted-foreground dark:text-white/70 max-w-2xl mx-auto">
                Transforme seu departamento de RH com soluções que realmente fazem a diferença.
              </p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
              {benefits.map((benefit, index) => (
                <Card key={benefit.title} className="bg-card/80 backdrop-blur-sm border-border shadow-md dark:shadow-none hover:-translate-y-2 transition-all duration-500">
                  <CardContent className="p-6 text-center">
                    <div className="inline-flex items-center justify-center w-16 h-16 rounded-2xl bg-gradient-to-br from-fs-cyan-500 to-fs-magenta-500 mb-4">
                      <benefit.icon className="w-8 h-8 text-white" />
                    </div>
                    <h3 className="text-lg font-bold text-foreground dark:text-white mb-3">
                      {benefit.title}
                    </h3>
                    <p className="text-muted-foreground dark:text-white/70 text-sm leading-relaxed">
                      {benefit.description}
                    </p>
                  </CardContent>
                </Card>
              ))}
            </div>
          </Container>
        </section>

        {/* Services Navigation */}
        <section className="py-12 bg-fs-section border-b border-border">
          <Container>
            <div className="flex flex-wrap justify-center gap-4">
              {services.map((service) => {
                const IconComponent = service.icon;
                return (
                  <button
                    key={service.id}
                    onClick={() => setActiveTab(service.id)}
                    className={`flex items-center space-x-3 px-6 py-3 rounded-xl transition-all duration-300 ${
                      activeTab === service.id
                        ? 'bg-gradient-to-r from-fs-cyan-500 to-fs-magenta-500 text-white scale-105'
                        : 'bg-card dark:bg-gray-900/50 text-foreground dark:text-white/70 hover:text-foreground dark:hover:text-white hover:bg-muted dark:hover:bg-gray-900/70'
                    }`}
                  >
                    <IconComponent className="w-5 h-5" />
                    <span className="font-medium">{service.title}</span>
                  </button>
                );
              })}
            </div>
          </Container>
        </section>

        {/* Active Service Details */}
        {activeService && (
          <section className="py-20 bg-fs-section">
            <Container>
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-start">
                {/* Left Column - Service Info */}
                <div>
                  <div className="flex items-center space-x-4 mb-6">
                    <div className={`w-16 h-16 rounded-2xl bg-gradient-to-br ${activeService.color} p-4`}>
                      <activeService.icon className="w-full h-full text-white" />
                    </div>
                    <div>
                      <h2 className="text-3xl font-bold font-poppins text-foreground dark:text-white">
                        {activeService.title}
                      </h2>
                      <p className="text-fs-cyan-400">{activeService.description}</p>
                    </div>
                  </div>

                  <div className="space-y-8">
                    {/* Overview */}
                    <div>
                      <h3 className="text-xl font-semibold text-foreground dark:text-white mb-4">Como Funciona</h3>
                      <p className="text-muted-foreground dark:text-white/70 leading-relaxed">
                        {activeService.details.overview}
                      </p>
                    </div>

                    {/* Features */}
                    <div>
                      <h3 className="text-xl font-semibold text-foreground dark:text-white mb-4">Principais Funcionalidades</h3>
                      <ul className="space-y-3">
                        {activeService.details.features.map((feature, index) => (
                          <li key={index} className="flex items-start space-x-3">
                            <CheckCircle className="w-5 h-5 text-fs-cyan-400 flex-shrink-0 mt-0.5" />
                            <span className="text-muted-foreground dark:text-white/70">{feature}</span>
                          </li>
                        ))}
                      </ul>
                    </div>

                    {/* Process Flow */}
                    <div>
                      <h3 className="text-xl font-semibold text-foreground dark:text-white mb-4">Fluxo do Processo</h3>
                      <div className="space-y-3">
                        {activeService.details.processes.map((process, index) => (
                          <div key={index} className="flex items-center space-x-3">
                            <div className="w-6 h-6 rounded-full bg-gradient-to-br from-fs-cyan-500 to-fs-magenta-500 flex items-center justify-center text-white text-xs font-bold">
                              {index + 1}
                            </div>
                            <span className="text-muted-foreground dark:text-white/70">{process}</span>
                          </div>
                        ))}
                      </div>
                    </div>
                  </div>
                </div>

                {/* Right Column - Benefits and Cases */}
                <div className="space-y-8">
                  {/* Benefits Card */}
                  <Card className="bg-card dark:bg-gray-900/50 backdrop-blur-sm border border-fs-purple-500/20">
                    <CardHeader>
                      <CardTitle className="flex items-center space-x-3 text-foreground dark:text-white">
                        <Zap className="w-6 h-6 text-fs-cyan-400" />
                        <span>Principais Benefícios</span>
                      </CardTitle>
                    </CardHeader>
                    <CardContent>
                      <ul className="space-y-3">
                        {activeService.details.benefits.map((benefit, index) => (
                          <li key={index} className="flex items-start space-x-3">
                            <div className="w-2 h-2 bg-fs-magenta-400 rounded-full flex-shrink-0 mt-2" />
                            <span className="text-muted-foreground dark:text-white/70">{benefit}</span>
                          </li>
                        ))}
                      </ul>
                    </CardContent>
                  </Card>

                  {/* ROI Card */}
                  <Card className="bg-gradient-to-br from-fs-cyan-500/20 to-fs-magenta-500/20 border border-fs-cyan-500/30">
                    <CardContent className="p-6">
                      <div className="text-center mb-4">
                        <TrendingUp className="w-12 h-12 text-fs-cyan-400 mx-auto mb-3" />
                        <h3 className="text-xl font-bold text-foreground dark:text-white mb-2">
                          Retorno sobre Investimento
                        </h3>
                      </div>
                      <div className="grid grid-cols-2 gap-4 text-center">
                        <div>
                          <div className="text-2xl font-bold fs-gradient-text">300%</div>
                          <div className="text-xs text-muted-foreground dark:text-white/70">ROI em 12 meses</div>
                        </div>
                        <div>
                          <div className="text-2xl font-bold fs-gradient-text">90%</div>
                          <div className="text-xs text-muted-foreground dark:text-white/70">Redução de tempo</div>
                        </div>
                      </div>
                    </CardContent>
                  </Card>

                  {/* CTA Card */}
                  <Card className="bg-gradient-to-br from-fs-magenta-500/20 to-fs-purple-500/20 border border-fs-magenta-500/30">
                    <CardContent className="p-6 text-center">
                      <h3 className="text-xl font-bold text-foreground dark:text-white mb-3">
                        Quer ver na prática?
                      </h3>
                      <p className="text-muted-foreground dark:text-white/70 mb-6">
                        Agende uma demonstração e veja como nossos processos 
                        podem transformar seu RH.
                      </p>
                      <div className="flex flex-col sm:flex-row gap-3">
                        <Button variant="fs-primary" className="flex-1">
                          <MonitorPlay className="w-4 h-4 mr-2" />
                          Ver Demonstração
                        </Button>
                        <Button variant="fs-outline" className="flex-1">
                          Solicitar Proposta
                        </Button>
                      </div>
                    </CardContent>
                  </Card>
                </div>
              </div>
            </Container>
          </section>
        )}

        {/* Integration Section */}
        <section className="py-20 bg-gradient-to-b from-fs-section to-fs-section/50">
          <Container>
            <div className="text-center mb-12">
              <h2 className="text-3xl md:text-4xl font-bold font-poppins text-foreground dark:text-white mb-4">
                Integração com
                <span className="block fs-gradient-text mt-2">
                  Folha de Pagamento
                </span>
              </h2>
              <p className="text-muted-foreground dark:text-white/70 max-w-2xl mx-auto mb-8">
                Nossos processos se integram automaticamente com os principais sistemas
                de folha de pagamento do mercado.
              </p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              {[
                {
                  title: "Integração Automática",
                  description: "Dados fluem automaticamente para a folha sem retrabalho",
                  icon: Zap,
                  color: "from-fs-cyan-500 to-fs-magenta-500"
                },
                {
                  title: "Dados Consistentes",
                  description: "Eliminação de divergências entre RH e folha de pagamento",
                  icon: Shield,
                  color: "from-fs-magenta-500 to-fs-purple-500"
                },
                {
                  title: "Conformidade Legal",
                  description: "Todos os cálculos seguem rigorosamente a legislação",
                  icon: Award,
                  color: "from-fs-purple-500 to-fs-cyan-500"
                }
              ].map((feature, index) => (
                <Card key={feature.title} className="bg-card dark:bg-gray-900/50 backdrop-blur-sm border border-fs-purple-500/20 hover:-translate-y-2 transition-all duration-500">
                  <CardContent className="p-6 text-center">
                    <div className={`inline-flex items-center justify-center w-16 h-16 rounded-2xl bg-gradient-to-br ${feature.color} mb-4`}>
                      <feature.icon className="w-8 h-8 text-white" />
                    </div>
                    <h3 className="text-lg font-bold text-foreground dark:text-white mb-3">
                      {feature.title}
                    </h3>
                    <p className="text-muted-foreground dark:text-white/70 text-sm leading-relaxed">
                      {feature.description}
                    </p>
                  </CardContent>
                </Card>
              ))}
            </div>
          </Container>
        </section>

        {/* Other Services Section */}
        <section className="py-20 bg-fs-section">
          <Container>
            <div className="text-center mb-12">
              <h2 className="text-3xl md:text-4xl font-bold font-poppins text-foreground dark:text-white mb-4">
                Outros Serviços
                <span className="block fs-gradient-text mt-2">
                  Especializados
                </span>
              </h2>
              <p className="text-muted-foreground dark:text-white/70 max-w-2xl mx-auto">
                Explore nossas outras áreas de expertise para uma solução completa.
              </p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              {[
                {
                  title: "Tecnologia da Informação",
                  description: "Outsourcing Fluig, desenvolvimento JavaScript/React e soluções Python.",
                  icon: MonitorPlay,
                  href: "/servicos/ti",
                  color: "from-fs-purple-500 to-fs-cyan-500"
                },
                {
                  title: "Gestão da Qualidade",
                  description: "Controle de não conformidades e gestão completa de documentos.",
                  icon: Shield,
                  href: "/servicos/qualidade",
                  color: "from-fs-magenta-500 to-fs-purple-500"
                },
                {
                  title: "Processos Financeiros",
                  description: "Automação de compras, fornecedores e reembolsos de despesas.",
                  icon: DollarSign,
                  href: "/servicos/financeiro",
                  color: "from-fs-cyan-500 to-fs-magenta-500"
                }
              ].map((service, index) => (
                <Link key={service.title} to={service.href}>
                  <Card className="group bg-card dark:bg-gray-900/50 backdrop-blur-sm border border-fs-purple-500/20 hover:-translate-y-2 transition-all duration-500 hover:shadow-fs-hover hover:border-fs-purple-500/50 h-full">
                    <CardContent className="p-6">
                      <div className={`w-12 h-12 rounded-xl bg-gradient-to-br ${service.color} p-3 mb-4 group-hover:scale-110 transition-transform duration-300`}>
                        <service.icon className="w-full h-full text-white" />
                      </div>
                      <h3 className="text-lg font-bold text-foreground dark:text-white mb-3 group-hover:fs-gradient-text transition-all duration-300">
                        {service.title}
                      </h3>
                      <p className="text-muted-foreground dark:text-white/70 text-sm leading-relaxed mb-4">
                        {service.description}
                      </p>
                      <Button variant="fs-outline" size="sm" className="w-full group-hover:bg-fs-primary/20">
                        Saiba Mais
                        <ArrowRight className="w-4 h-4 ml-2 group-hover:translate-x-1 transition-transform" />
                      </Button>
                    </CardContent>
                  </Card>
                </Link>
              ))}
            </div>
          </Container>
        </section>

        {/* Related Links */}
        <section className="py-20 bg-gradient-to-b from-fs-section to-fs-section/50">
          <div className="max-w-6xl mx-auto px-6">
            <RelatedLinks 
              title="Outros Serviços Especializados"
              links={[
                {
                  title: "Serviços de TI Especializados",
                  description: "Outsourcing Fluig, desenvolvimento JavaScript/React e soluções Python com SLA garantido de 99.9%.",
                  href: "/servicos/ti",
                  category: "Serviços"
                },
                {
                  title: "Gestão da Qualidade",
                  description: "Controle de não conformidades, gestão de documentos e processos de qualidade automatizados.",
                  href: "/servicos/qualidade",
                  category: "Serviços"
                },
                {
                  title: "Automação de Processos",
                  description: "Transforme processos manuais em workflows automatizados com nossa expertise em BPM.",
                  href: "/automacao-processos",
                  category: "Automação"
                },
                {
                  title: "Consultoria Fluig",
                  description: "Maximize o ROI da sua plataforma com nossa consultoria estratégica e 50.000+ horas de experiência.",
                  href: "/consultoria-fluig",
                  category: "Consultoria"
                }
              ]}
            />
          </div>
        </section>

        {/* FAQ Section */}
        <section className="py-20">
          <Container>
            <FAQSection 
              title="Perguntas Frequentes - Recursos Humanos"
              description="Esclareça suas dúvidas sobre nossos serviços de Recursos Humanos"
              faqs={[
                {
                  question: "Como funciona o processo de admissão digital?",
                  answer: "Nosso processo de admissão digital permite que novos colaboradores enviem todos os documentos necessários através de um portal online seguro. O sistema valida automaticamente os documentos, cria checklists personalizados e integra diretamente com a folha de pagamento. O processo completo pode ser finalizado em até 24 horas, eliminando a necessidade de papelada física e garantindo compliance total com a legislação trabalhista."
                },
                {
                  question: "O sistema de gestão de férias se integra com a folha de pagamento?",
                  answer: "Sim, nossa solução de gestão de férias possui integração nativa com os principais sistemas de folha de pagamento do mercado (TOTVS, SAP, Oracle). Todas as solicitações aprovadas são automaticamente enviadas para o sistema de folha, incluindo cálculos de abono pecuniário, adiantamentos e demais verbas. Isso elimina retrabalho e garante que não haja divergências entre os sistemas de RH e folha."
                },
                {
                  question: "Quanto tempo demora para implementar os processos de RH?",
                  answer: "O tempo de implementação varia conforme a complexidade e quantidade de processos. Processos simples como solicitação de férias podem ser implementados em 1-2 semanas. Já implementações completas incluindo admissão digital, gestão de férias e desligamentos levam entre 4-8 semanas. Utilizamos metodologia ágil com entregas incrementais, permitindo que você comece a usar os processos conforme ficam prontos."
                },
                {
                  question: "Os processos são compatíveis com a legislação trabalhista brasileira?",
                  answer: "Absolutamente. Todos os nossos processos de RH são desenvolvidos em conformidade com a CLT e demais normativos trabalhistas brasileiros. Mantemos uma equipe jurídica especializada que acompanha constantemente as mudanças na legislação e atualiza automaticamente os workflows quando necessário. Oferecemos garantia total de compliance para auditorias trabalhistas e fiscalizações."
                },
                {
                  question: "Existe versão mobile dos processos de RH?",
                  answer: "Sim, todos os processos de RH possuem interface responsiva e aplicativo mobile dedicado. Colaboradores podem solicitar férias, enviar atestados, consultar holerites e realizar outras atividades diretamente pelo smartphone. Gestores recebem notificações push para aprovações urgentes e podem gerenciar suas equipes remotamente. O app funciona offline para consultas e sincroniza automaticamente quando conectado."
                },
                {
                  question: "Como é feita a gestão de usuários e permissões no sistema?",
                  answer: "O sistema possui controle granular de permissões baseado em perfis e hierarquia organizacional. Colaboradores têm acesso apenas aos seus próprios processos, gestores aprovam demandas de suas equipes, e o RH possui visão completa com relatórios gerenciais. A integração com Active Directory permite login único (SSO) e sincronização automática de usuários. Também oferecemos auditoria completa de todas as ações realizadas no sistema."
                }
              ]}
            />
          </Container>
        </section>
      </main>

      <Footer />
      <WhatsAppFloat />
    </div>
  );
};

export default ServicesRH;